"use client";

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  forwardRef,
} from "react";
import { Autocomplete, CircularProgress, TextField, TextFieldProps } from "@mui/material";
import { debounce } from "lodash";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import { ICompanyOutlet } from "../../redux/companyOutlet/companyOutlet.types";

export interface ICompanyAutoSearch {
  label?: string;
  value: string | null;
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  onSelect: (value: ICompanyOutlet | null) => void;
  company_outlet_name?: string;
  size?: "standard" | "large"
}

export const CompanyAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  ICompanyAutoSearch
>((props, ref) => {
  const { value, disabled, label, onSelect } = props;
  const [options, setOptions] = useState<readonly ICompanyOutlet[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearchText] = useState<string>("");
  const [key, setKey] = React.useState(0);

  const fetchSuggestion = useCallback(async (enteredValue: string) => {
    setLoading(true);
    try {
      let query = "";
      if (enteredValue) {
        query = `&columns=company_outlet_name&value=${encodeURIComponent(enteredValue)}`;
      }
      const url = `/dataManagement/get-company-outlet?status=ACTIVE${query}`;

      const res = await api.get(url);
      const data: ICompanyOutlet[] = res.data.data;

      setOptions(data);
    } catch (error) {
      console.error("❌ CompanyAutoSearch API error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetchSuggestion = useMemo(
    () =>
      debounce((enteredValue: string) => {
        if (enteredValue.length >= 2 || enteredValue.length === 0) {
          fetchSuggestion(enteredValue);
        }
      }, 500),
    [fetchSuggestion]
  );

  useEffect(() => {
    debouncedFetchSuggestion(search);
  }, [search, debouncedFetchSuggestion]);

  const clear = useCallback(() => {
    setSearchText("");
    setKey((prev) => prev + 1);
    onSelect(null);
  }, [onSelect]);

  useImperativeHandle(ref, () => ({
    clear,
  }));

  const selectedOption = options.find(
    (option) => option.company_outlet_uuid === value
  );

  const InputComponent = props.size === "large" ? TextField : CustomTextField;

  return (
    <>
      {/* {label && <CustomFormLabel>{label}</CustomFormLabel>} */}
      <Autocomplete

        key={key}
        fullWidth
        sx={{
          ".MuiOutlinedInput-root": {
            ...(props.size !== "large" && {
              paddingTop: "0px",
              paddingBottom: "0px",
            }),
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        getOptionLabel={(option) => option.company_outlet_name || ""}
        options={options}
        value={selectedOption || null}
        isOptionEqualToValue={(option, value) =>
          option.company_outlet_uuid === value?.company_outlet_uuid
        }
        loading={loading}
        disabled={disabled}
        noOptionsText="No company outlets available"
        onChange={(event, newValue) => {
          onSelect(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setSearchText(newInputValue);
        }}
        renderInput={(params) => (
          <InputComponent
            {...params}
            label={label}
            fullWidth
            placeholder="Search company outlet..."
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true
            }}
            sx={{

            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
});

CompanyAutoSearch.displayName = "CompanyAutoSearch";
